import { ChangeEvent, FC, ReactNode } from 'react';
import { Button, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { FontWeights } from 'shared';

type TokenInputProps = {
  label?: string;
  value?: string | number;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  tokenIcon?: ReactNode;
  tokenSymbol?: string;
  tokenBalance?: string;
  error?: string;
  helperText?: string;
  onMaxClick?: () => void;
  tokenComponent?: ReactNode;
};

export const TokenInput: FC<TokenInputProps> = ({
  label,
  value,
  onChange,
  tokenIcon,
  tokenSymbol,
  tokenBalance,
  error,
  helperText,
  onMaxClick,
  tokenComponent,
}) => {
  const isBalancePositive = Number(tokenBalance) !== 0;

  return (
    <TextField
      label={label}
      value={value}
      onChange={onChange}
      className="token"
      error={!!error}
      helperText={
        helperText ? (
          <Typography variant="caption" className="gray" noWrap>
            {helperText}
          </Typography>
        ) : (
          error
        )
      }
      InputProps={{
        endAdornment: (
          <Stack
            spacing={0.25}
            alignItems="flex-end"
            sx={{
              position: 'absolute',
              right: '1rem',
            }}
          >
            {tokenBalance != null && (
              <Typography variant="caption" className="gray" noWrap sx={{ maxWidth: 200 }}>
                Balance:
              </Typography>
            )}
            <Stack sx={(theme) => ({ background: theme.themeColors.colorTextFieldBackground })}>
              {tokenComponent || (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing="10px"
                  sx={(theme) => ({
                    background: theme.themeColors.colorTextFieldTokenBackground,
                    borderRadius: '155px',
                    p: theme.spacing(0.5, 1.5, 0.5, 0.5),
                  })}
                >
                  {tokenIcon}
                  <Typography variant="button" className="white" fontWeight={FontWeights.Medium} lineHeight="19px">
                    {tokenSymbol || '...'}
                  </Typography>
                </Stack>
              )}
            </Stack>
            {tokenBalance != null && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                {...(isBalancePositive && { justifyContent: 'space-between', sx: { minWidth: '100%' } })}
              >
                {isBalancePositive && (
                  <Button variant="text" size="small" onClick={onMaxClick}>
                    <Typography variant="caption" noWrap sx={{ color: 'inherit', lineHeight: '18px', fontSize: 12 }}>
                      Max
                    </Typography>
                  </Button>
                )}
                <Tooltip
                  title={
                    <Typography component="span" sx={(theme) => ({ color: theme.themeColors.colorTooltipText })}>
                      Balance:{' '}
                      <Typography
                        component="span"
                        sx={(theme) => ({ color: theme.themeColors.colorTooltipTextAccent })}
                      >
                        {tokenBalance}
                      </Typography>
                    </Typography>
                  }
                >
                  <Typography variant="caption" className="gray" noWrap sx={{ maxWidth: 200 }}>
                    {tokenBalance || '0'}
                  </Typography>
                </Tooltip>
              </Stack>
            )}
          </Stack>
        ),
      }}
      sx={{
        '&.token': {
          '.MuiFormHelperText-root': {
            fontSize: {
              sm: '12px',
              xs: '9px',
            },
          },
        },
      }}
    />
  );
};
